import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { formatBytes } from "../lib/format";

// Carte "plan & quotas" : plan courant + usage (radios X/max, stockage utilisé/quota)
// + lien "Améliorer" vers la section tarifs. Peut recevoir un `plan` déjà chargé
// (évite un double appel réseau) sinon le récupère lui-même via GET /api/me/plan.
export default function PlanBanner({ plan: planProp }) {
  const { t } = useTranslation();
  const [plan, setPlan] = useState(planProp || null);

  useEffect(() => {
    if (planProp) { setPlan(planProp); return; }
    let mounted = true;
    api.get("/api/me/plan")
      .then((r) => { if (mounted) setPlan(r.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [planProp]);

  if (!plan) return null;

  const quota = plan.quota || {};
  const usage = plan.usage || {};
  const maxRadios = quota.maxRadios ?? 0;
  const usedRadios = usage.radios ?? 0;
  const storageQuota = quota.storageBytes ?? 0;
  const storageUsed = usage.storageBytes ?? 0;
  const storagePct = storageQuota > 0 ? Math.min(100, (storageUsed / storageQuota) * 100) : 0;
  const planName = (plan.plan || "free").charAt(0).toUpperCase() + (plan.plan || "free").slice(1);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 text-white flex items-center justify-center text-lg">✦</span>
          <div>
            <p className="text-xs text-gray-500">{t("plan.yourPlan")}</p>
            <p className="font-semibold text-gray-900">{planName}</p>
          </div>
        </div>
        <a
          href="/#pricing"
          className="text-sm font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 px-4 py-2 rounded-md"
        >
          {t("myRadios.upgrade")}
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>{t("plan.radios")}</span>
            <span className="font-medium text-gray-900">{usedRadios} / {maxRadios}</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-purple-500"
              style={{ width: maxRadios > 0 ? `${Math.min(100, (usedRadios / maxRadios) * 100)}%` : "0%" }}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>{t("plan.storage")}</span>
            <span className="font-medium text-gray-900">{formatBytes(storageUsed)} / {formatBytes(storageQuota)}</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full ${storagePct >= 90 ? "bg-red-500" : "bg-blue-500"}`}
              style={{ width: `${storagePct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
